
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight, X, Shield, Zap, Globe } from 'lucide-react';
import { Button } from '@shared/components/ui';
import { ROUTES } from '@app/routes';

interface PricingPageProps {
  onBack?: () => void;
  onGetStarted?: () => void;
}

const PricingCard: React.FC<{
  tier: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  recommended?: boolean;
  onAction: () => void;
}> = ({ tier, price, period, description, features, recommended, onAction }) => (
  <div className={`
    relative flex flex-col p-6 sm:p-8 rounded-xl border transition-all duration-300 group
    ${recommended
      ? 'bg-gradient-to-br from-dark-surface to-dark-bg/80 border-brand-500/60 shadow-2xl shadow-brand-900/30 md:scale-105 z-10'
      : 'bg-gradient-to-br from-dark-surface/80 to-dark-bg/60 border-dark-border/60 hover:border-dark-border hover:shadow-xl'}
  `}>
    {recommended && (
      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-brand-600 to-brand-500 text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider shadow-lg shadow-brand-900/30">
        Most Popular
      </div>
    )}

    <div className="mb-6">
      <h3 className={`text-lg sm:text-xl font-bold mb-2 ${recommended ? 'text-white' : 'text-gray-300'}`}>{tier}</h3>
      <div className="flex items-baseline gap-1">
        <span className="text-3xl sm:text-4xl font-bold text-white">{price}</span>
        <span className="text-sm text-gray-500">{period}</span>
      </div>
      <p className="text-sm sm:text-base text-gray-400 mt-4 leading-relaxed">{description}</p>
    </div>

    <div className="flex-1 space-y-4 mb-8">
      {features.map((feature, i) => (
        <div key={i} className="flex items-start gap-3 text-sm sm:text-base">
          <div className={`mt-0.5 p-0.5 rounded-full flex-shrink-0 ${recommended ? 'bg-brand-500/20 text-brand-400' : 'bg-gray-800 text-gray-400'}`}>
            <Check size={12} strokeWidth={3} />
          </div>
          <span className="text-gray-300">{feature}</span>
        </div>
      ))}
    </div>

    <button
      onClick={onAction}
      className={`
        w-full py-3 sm:py-3.5 rounded-lg font-bold text-sm sm:text-base transition-all duration-200 flex items-center justify-center gap-2 min-h-[44px]
        ${recommended
          ? 'bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-brand-400 text-white shadow-md shadow-brand-900/20 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]'
          : 'bg-dark-surface hover:bg-dark-hover text-white border border-dark-border/60 hover:border-dark-border'}
      `}
    >
      Get Started <ArrowRight size={16} />
    </button>
  </div>
);

export const PricingPage: React.FC<PricingPageProps> = ({ onBack, onGetStarted }) => {
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(true);

  const handleBack = () => onBack ? onBack() : navigate(ROUTES.HOME);
  const handleGetStarted = () => onGetStarted ? onGetStarted() : navigate(ROUTES.LOGIN);

  return (
    <div className="min-h-screen bg-[#0f1115] text-gray-200 font-sans overflow-x-hidden flex flex-col">
      
      {/* Header */}
      <header className="px-4 sm:px-6 md:px-12 py-4 sm:py-6 flex justify-between items-center border-b border-[#1f232b] bg-[#0f1115]/90 backdrop-blur sticky top-0 z-50">
        <div className="flex items-center gap-2 cursor-pointer" onClick={handleBack}>
          <div className="w-8 h-8 bg-brand-600 rounded-md flex items-center justify-center">
            <span className="font-bold text-white text-lg">B</span>
          </div>
          <span className="text-base sm:text-xl font-bold tracking-tight text-white">Benefits<span className="text-brand-400">Outreach</span>360</span>
        </div>
        <div className="flex items-center gap-3 sm:gap-6">
          <button onClick={handleBack} className="hidden sm:block text-sm text-gray-400 hover:text-white transition-colors">
            Back to Home
          </button>
          <Button onClick={handleGetStarted} variant="primary" className="bg-brand-600 hover:bg-brand-500 text-white px-4 sm:px-6 py-2 text-sm sm:text-base min-h-[44px]">
            Login
          </Button>
        </div>
      </header>

      <main className="flex-1 py-12 sm:py-20 px-4 sm:px-6">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight leading-tight">
            Transparent pricing for <br className="hidden sm:block"/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-400 to-blue-500">modern outreach teams</span>
          </h1>
          <p className="text-base sm:text-lg text-gray-400 mb-10 px-4">
            Choose the plan that fits your scale. Whether you're a solo broker or a national agency, we have the tools to automate your workflow.
          </p>

          {/* Toggle */}
          <div className="flex items-center justify-center gap-3 sm:gap-4 mb-8">
            <span className={`text-sm sm:text-base font-medium ${!isAnnual ? 'text-white' : 'text-gray-500'}`}>Monthly</span>
            <button
              className="w-16 h-9 bg-[#1f232b] rounded-full p-1.5 cursor-pointer border border-[#2a2e37] min-h-[44px] flex items-center"
              onClick={() => setIsAnnual(!isAnnual)}
              aria-label="Toggle pricing period"
            >
              <div className={`w-6 h-6 bg-brand-500 rounded-full shadow-md transform transition-transform duration-300 ${isAnnual ? 'translate-x-7' : 'translate-x-0'}`} />
            </button>
            <span className={`text-sm sm:text-base font-medium ${isAnnual ? 'text-white' : 'text-gray-500'}`}>
              Yearly <span className="text-brand-400 text-xs ml-1">(Save 20%)</span>
            </span>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 items-start">
          
          <PricingCard
            tier="Starter"
            price={isAnnual ? "$49" : "$59"}
            period="/mo"
            description="Perfect for individual agents just getting started with automated drops."
            features={[
              "Up to 500 Contacts",
              "Basic Route Optimization",
              "PDF Label Generation",
              "Email Support",
              "1 User Seat"
            ]}
            onAction={handleGetStarted}
          />

          <PricingCard
            tier="Professional"
            price={isAnnual ? "$149" : "$179"}
            period="/mo"
            recommended={true}
            description="For growing teams that need advanced tracking and CRM integration."
            features={[
              "Up to 5,000 Contacts",
              "Advanced Multi-Day Routing",
              "Pipedrive 2-Way Sync",
              "QR Code Tracking",
              "Priority Support",
              "5 User Seats"
            ]}
            onAction={handleGetStarted}
          />

          <PricingCard
            tier="Enterprise"
            price="Custom"
            period=""
            description="Full-scale automation for national agencies and large organizations."
            features={[
              "Unlimited Contacts",
              "Custom API Integration",
              "Dedicated Account Manager",
              "SLA & Uptime Guarantee",
              "SSO / SAML Support",
              "Unlimited Seats"
            ]}
            onAction={handleGetStarted}
          />

        </div>

        {/* Feature Comparison / Trust Section */}
        <div className="max-w-5xl mx-auto mt-20 sm:mt-32 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-8 sm:mb-12 px-4">Trusted by industry leaders</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
             <div className="p-6 sm:p-8 bg-gradient-to-br from-dark-surface to-dark-bg/80 rounded-xl border border-dark-border/60 transition-all duration-200 hover:shadow-xl hover:border-brand-500/30">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-brand-500/20 to-brand-600/10 border border-brand-500/30 flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-7 h-7 text-brand-400" />
                </div>
                <h3 className="text-white font-bold mb-2 text-base sm:text-lg">SOC 2 Compliant</h3>
                <p className="text-sm sm:text-base text-dark-muted leading-relaxed">Your data is secure with enterprise-grade encryption and compliance standards.</p>
             </div>
             <div className="p-6 sm:p-8 bg-gradient-to-br from-dark-surface to-dark-bg/80 rounded-xl border border-dark-border/60 transition-all duration-200 hover:shadow-xl hover:border-yellow-500/30">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 border border-yellow-500/30 flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-7 h-7 text-yellow-400" />
                </div>
                <h3 className="text-white font-bold mb-2 text-base sm:text-lg">99.9% Uptime</h3>
                <p className="text-sm sm:text-base text-dark-muted leading-relaxed">Redundant infrastructure ensures your campaigns never stop running.</p>
             </div>
             <div className="p-6 sm:p-8 bg-gradient-to-br from-dark-surface to-dark-bg/80 rounded-xl border border-dark-border/60 sm:col-span-2 md:col-span-1 transition-all duration-200 hover:shadow-xl hover:border-blue-500/30">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-7 h-7 text-blue-400" />
                </div>
                <h3 className="text-white font-bold mb-2 text-base sm:text-lg">Global Routing</h3>
                <p className="text-sm sm:text-base text-dark-muted leading-relaxed">Optimize routes across borders with Google Maps Platform integration.</p>
             </div>
          </div>
        </div>

      </main>

      {/* Footer */}
      <footer className="border-t border-[#1f232b] bg-[#0f1115] py-8 sm:py-12 mt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 text-center text-gray-600 text-xs sm:text-sm">
          &copy; 2024 Benefits Outreach 360. All rights reserved.
        </div>
      </footer>
    </div>
  );
};
